import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Button,
    DialogActions,
    Box,
    Paper,
    Typography,
    IconButton,
    InputAdornment,
    MenuItem,
    Tooltip,
    Stack,
    Chip,
    Alert,
    Divider,
} from "@mui/material";
import {
    AddCircle as AddCircleIcon,
    RemoveCircle as RemoveCircleIcon,
    Search as SearchIcon,
    DragIndicator as DragIndicatorIcon,
    AccessTime as AccessTimeIcon,
} from "@mui/icons-material";

import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import {
    DndContext,
    closestCenter,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    useSortable,
    arrayMove,
    verticalListSortingStrategy,
    sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { useProjects } from "../../../hooks/useProjects";
import { useLocalizaciones } from "../../../hooks/useLocalizations";
import LocalizationForm from "../localizaciones/LocalizationForm";

const schema = yup.object().shape({
    rutnom: yup.string().trim().required("El nombre de la ruta es obligatorio"),
    rutdes: yup.string().max(255, "Máximo 255 caracteres").nullable(),
    proid: yup.mixed().nullable(),
});

const normalizeStops = (paradas = []) =>
    [...paradas]
        .sort((a, b) => Number(a.parord || 0) - Number(b.parord || 0))
        .map((p) => ({
            locid: p.locid,
            locnom: p.locnom,
            parhorest: p.parhorest || "",
        }));

const getStopType = (index, total) => {
    if (index === 0) return "ORIGEN";
    if (index === total - 1) return "DESTINO";
    return "INTERMEDIA";
};

const SortableStopItem = ({ stop, index, total, onRemove, onTimeChange }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: String(stop.locid) });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.65 : 1,
        zIndex: isDragging ? 10 : "auto",
    };

    const type = getStopType(index, total);

    return (
        <Paper
            ref={setNodeRef}
            elevation={isDragging ? 4 : 1}
            sx={{
                p: 1.5,
                mb: 1.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor:
                    type === "ORIGEN"
                        ? "success.light"
                        : type === "DESTINO"
                            ? "error.light"
                            : "divider",
                bgcolor: "background.paper",
                ...style,
            }}
        >
            <Stack direction="row" spacing={1.5} alignItems="center">
                <IconButton
                    size="small"
                    {...attributes}
                    {...listeners}
                    sx={{ cursor: "grab" }}
                >
                    <DragIndicatorIcon />
                </IconButton>

                <Box flex={1}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography fontWeight={600}>
                            #{index + 1} - {stop.locnom}
                        </Typography>

                        <Chip
                            size="small"
                            label={type}
                            color={
                                type === "ORIGEN"
                                    ? "success"
                                    : type === "DESTINO"
                                        ? "error"
                                        : "default"
                            }
                        />
                    </Stack>

                    <TextField
                        type="time"
                        size="small"
                        label="Hora estimada"
                        value={stop.parhorest || ""}
                        onChange={(e) => onTimeChange(stop.locid, e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <AccessTimeIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mt: 1, width: 180 }}
                    />
                </Box>

                <Tooltip title="Quitar parada">
                    <IconButton color="error" onClick={() => onRemove(stop.locid)}>
                        <RemoveCircleIcon />
                    </IconButton>
                </Tooltip>
            </Stack>
        </Paper>
    );
};

const RouteForm = ({ open, handleClose, onSubmit, defaultValues = {} }) => {
    const {
        register,
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            rutnom: "",
            rutdes: "",
            proid: "",
        },
    });

    const { projects = [] } = useProjects();
    const { localizaciones = [] } = useLocalizaciones();

    const [selectedStops, setSelectedStops] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [openLocModal, setOpenLocModal] = useState(false);
    const [submitError, setSubmitError] = useState("");

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 6 },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        if (!open) return;

        reset({
            rutnom: defaultValues?.rutnom || "",
            rutdes: defaultValues?.rutdes || "",
            proid: defaultValues?.proid || "",
        });

        setSelectedStops(normalizeStops(defaultValues?.paradas || []));
        setSearchTerm("");
        setSubmitError("");
    }, [open, defaultValues, reset]);

    const selectedIds = useMemo(
        () => new Set(selectedStops.map((s) => String(s.locid))),
        [selectedStops]
    );

    const availableStops = useMemo(() => {
        return localizaciones
            .filter((loc) => !selectedIds.has(String(loc.locid)))
            .filter((loc) =>
                String(loc.locnom || "")
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase())
            );
    }, [localizaciones, selectedIds, searchTerm]);

    const handleAddStop = (loc) => {
        setSelectedStops((prev) => [
            ...prev,
            {
                locid: loc.locid,
                locnom: loc.locnom,
                parhorest: "",
            },
        ]);
    };

    const handleRemoveStop = (locid) => {
        setSelectedStops((prev) => prev.filter((s) => s.locid !== locid));
    };

    const handleTimeChange = (locid, time) => {
        setSelectedStops((prev) =>
            prev.map((stop) =>
                stop.locid === locid ? { ...stop, parhorest: time } : stop
            )
        );
    };

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;

        setSelectedStops((prev) => {
            const oldIndex = prev.findIndex((s) => String(s.locid) === active.id);
            const newIndex = prev.findIndex((s) => String(s.locid) === over.id);

            if (oldIndex === -1 || newIndex === -1) return prev;

            return arrayMove(prev, oldIndex, newIndex);
        });
    };

    const handleFormSubmit = (data) => {
        setSubmitError("");

        if (selectedStops.length < 2) {
            setSubmitError("Debe seleccionar al menos 2 paradas para crear la ruta.");
            return;
        }

        const paradas = selectedStops.map((loc, index, arr) => ({
            locid: loc.locid,
            parord: index + 1,
            partip: getStopType(index, arr.length),
            parhorest: loc.parhorest || null,
        }));

        onSubmit({
            ...data,
            proid: data.proid || null,
            paradas,
        });
    };

    const handleCreateLocation = (savedLocation) => {
        if (!savedLocation?.locid) {
            setOpenLocModal(false);
            return;
        }

        setSelectedStops((prev) => [
            ...prev,
            {
                locid: savedLocation.locid,
                locnom: savedLocation.locnom,
                parhorest: "",
            },
        ]);

        setOpenLocModal(false);
    };

    return (
        <>
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
                <DialogTitle>
                    {defaultValues?.rutid ? "Editar Ruta" : "Crear Ruta"}
                </DialogTitle>

                <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
                    <DialogContent dividers>
                        <Stack spacing={2}>
                            {submitError && <Alert severity="warning">{submitError}</Alert>}

                            <TextField
                                label="Nombre de la Ruta"
                                fullWidth
                                {...register("rutnom")}
                                error={!!errors.rutnom}
                                helperText={errors.rutnom?.message}
                            />

                            <TextField
                                label="Descripción"
                                fullWidth
                                multiline
                                rows={2}
                                {...register("rutdes")}
                                error={!!errors.rutdes}
                                helperText={errors.rutdes?.message}
                            />

                            <Controller
                                name="proid"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        select
                                        label="Proyecto"
                                        fullWidth
                                        error={!!errors.proid}
                                        helperText={errors.proid?.message}
                                        value={field.value ?? ""}
                                        onChange={(e) =>
                                            field.onChange(e.target.value === "" ? null : e.target.value)
                                        }
                                    >
                                        <MenuItem value="">Sin selección</MenuItem>
                                        {projects.map((proj) => (
                                            <MenuItem key={proj.proid} value={proj.proid}>
                                                {proj.pronom}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />

                            <Divider />

                            <Box
                                display="grid"
                                gridTemplateColumns={{ xs: "1fr", md: "1.25fr 1fr" }}
                                gap={2}
                            >
                                <Paper sx={{ p: 2, borderRadius: 2 }} variant="outlined">
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        mb={2}
                                    >
                                        <Box>
                                            <Typography variant="h6">
                                                Secuencia de paradas
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Arrastra para reordenar. La primera será origen y la última destino.
                                            </Typography>
                                        </Box>

                                        <Chip
                                            label={`${selectedStops.length} paradas`}
                                            color={selectedStops.length >= 2 ? "primary" : "default"}
                                        />
                                    </Stack>

                                    {selectedStops.length === 0 ? (
                                        <Alert severity="info">
                                            Agrega localizaciones desde la lista disponible.
                                        </Alert>
                                    ) : (
                                        <DndContext
                                            sensors={sensors}
                                            collisionDetection={closestCenter}
                                            onDragEnd={handleDragEnd}
                                        >
                                            <SortableContext
                                                items={selectedStops.map((s) => String(s.locid))}
                                                strategy={verticalListSortingStrategy}
                                            >
                                                {selectedStops.map((stop, index) => (
                                                    <SortableStopItem
                                                        key={stop.locid}
                                                        stop={stop}
                                                        index={index}
                                                        total={selectedStops.length}
                                                        onRemove={handleRemoveStop}
                                                        onTimeChange={handleTimeChange}
                                                    />
                                                ))}
                                            </SortableContext>
                                        </DndContext>
                                    )}
                                </Paper>

                                <Paper sx={{ p: 2, borderRadius: 2 }} variant="outlined">
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        mb={1}
                                    >
                                        <Typography variant="h6">
                                            Localizaciones disponibles
                                        </Typography>

                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => setOpenLocModal(true)}
                                        >
                                            Nueva
                                        </Button>
                                    </Stack>

                                    <TextField
                                        placeholder="Buscar localización..."
                                        size="small"
                                        fullWidth
                                        margin="dense"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    <Box mt={1.5} maxHeight={430} overflow="auto">
                                        {availableStops.length === 0 ? (
                                            <Alert severity="info">
                                                No hay localizaciones disponibles.
                                            </Alert>
                                        ) : (
                                            availableStops.map((loc) => (
                                                <Paper
                                                    key={loc.locid}
                                                    variant="outlined"
                                                    sx={{
                                                        p: 1,
                                                        mb: 1,
                                                        borderRadius: 2,
                                                    }}
                                                >
                                                    <Stack
                                                        direction="row"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        spacing={1}
                                                    >
                                                        <Typography>{loc.locnom}</Typography>

                                                        <Tooltip title="Agregar parada">
                                                            <IconButton
                                                                color="primary"
                                                                onClick={() => handleAddStop(loc)}
                                                            >
                                                                <AddCircleIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </Paper>
                                            ))
                                        )}
                                    </Box>
                                </Paper>
                            </Box>
                        </Stack>
                    </DialogContent>

                    <DialogActions>
                        <Button onClick={handleClose}>Cancelar</Button>
                        <Button type="submit" variant="contained">
                            {defaultValues?.rutid ? "Actualizar" : "Guardar"}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {openLocModal && (
                <LocalizationForm
                    open={openLocModal}
                    handleClose={() => setOpenLocModal(false)}
                    onSubmit={handleCreateLocation}
                />
            )}
        </>
    );
};

export default RouteForm;